import React, { useMemo, useState } from 'react';

import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { Select, Tooltip } from '@bringg/react-components';
import classNames from 'classnames';
import { DeliveryBlockResourceLocationType } from '@bringg/types/types/delivery_block';
import { ServiceArea, Vehicle } from '@bringg/types';
import { SortByStringWithNumber } from '@bringg-frontend/utils';

import { filterOption } from 'bringg-web/services/utils';
import { Option, OptGroup } from '../../../../components';
import { DefaultLocation, ParkingSpotLocation, ConfiguredDefaultLocation, UNKNOWN_ID } from '../hooks/use-resources';
import Driver from '../../../../stores/drivers/domain-object/driver';

const dropdownStyle = {
	dropdownMatchSelectWidth: false
};

const NAME_LENGTH_LIMIT = 14;
const NAME_LENGTH_LIMIT_WITH_AREAS = 12;

export const getLocationsSelect = (
	locations: Map<number, ParkingSpotLocation | DefaultLocation>,
	row,
	value,
	defaultType: ConfiguredDefaultLocation,
	hasHomeAddress: (driverId?: number) => boolean,
	hasParkingLocation: (vehicleId?: number) => boolean,
	onSelect,
	defaultLocationsTitle,
	parkingSpotsTitle,
	itemClassName
) => {
	const defaultValue = null;
	const userId = row.original.resource.user_id;
	const vehicleId = row.original.resource.vehicle_id;
	const isDriverHomeLocationDisabled = location => {
		return (
			location.id === DeliveryBlockResourceLocationType.DRIVER_ADDRESS &&
			((location.defaultLocation && !userId) || !hasHomeAddress(userId))
		);
	};
	const isVehicleParkingLocationDisabled = location => {
		return (
			location.id === DeliveryBlockResourceLocationType.VEHICLE_DEFAULT_PARKING &&
			((location.defaultLocation && !vehicleId) || !hasParkingLocation(vehicleId))
		);
	};
	const isDefaultLocationDisabled = location => {
		return location.defaultLocation && location.id === UNKNOWN_ID && defaultType.isFallback;
	};
	const isOptionDisabled = location =>
		isDriverHomeLocationDisabled(location) ||
		isVehicleParkingLocationDisabled(location) ||
		isDefaultLocationDisabled(location);

	const getLocationOption = (locationList: (ParkingSpotLocation | DefaultLocation)[]) => {
		return locationList.map((location: ParkingSpotLocation | DefaultLocation) => (
			<Option
				disabled={isOptionDisabled(location)}
				key={location.id}
				value={location.id}
				data-test-id={`${location.id}-dropdown-option`}
			>
				<span className={itemClassName} data-test-id={itemClassName}>
					{location.name}
				</span>
			</Option>
		));
	};

	const defaultLocations = Array.from(locations.values()).filter(location => location.defaultLocation);
	const parkingSpots = Array.from(locations.values()).filter(location => !location.defaultLocation);

	return (
		<div className="location-item-container">
			<BringgIcon iconName={BringgFontIcons.Location} />
			<Select
				onSelect={option => onSelect(option)}
				defaultValue={defaultValue}
				value={value?.id}
				bordered={false}
			>
				<OptGroup label={defaultLocationsTitle}>{getLocationOption(defaultLocations)}</OptGroup>
				{parkingSpots.length > 0 && (
					<OptGroup label={parkingSpotsTitle}>{getLocationOption(parkingSpots)}</OptGroup>
				)}
			</Select>
		</div>
	);
};

interface VehiclesSelectProps {
	vehicles: Map<number, Vehicle>;
	selectedVehicleIds: Set<number>;
	value: any;
	onSelect: (id: number) => void;
	defaultId: number;
	translations: { tooltipText: string; defaultTrailer?: string; defaultText?: string };
	unavailableVehicleIds: Set<number>;
	unavailableTrailerIds: Set<number>;
	itemClassName: string;
	isDisabled?: boolean;
	trailerIdAttachedToVehicle?: number | undefined;
	withServiceAreas?: boolean;
}

export const VehiclesSelect = ({
	vehicles,
	selectedVehicleIds,
	value,
	onSelect,
	defaultId,
	translations,
	unavailableVehicleIds,
	unavailableTrailerIds,
	itemClassName,
	isDisabled = false,
	trailerIdAttachedToVehicle = undefined,
	withServiceAreas = false
}: VehiclesSelectProps) => {
	const defaultValue = vehicles.get(defaultId)?.id;
	const [showTooltip, setShowTooltip] = useState(false);
	const vehicleByValue = vehicles.get(value?.id);
	const nameLimit = withServiceAreas ? NAME_LENGTH_LIMIT_WITH_AREAS : NAME_LENGTH_LIMIT;

	return (
		<div className="vehicle-item-container">
			<Tooltip
				title={vehicleByValue?.name}
				open={showTooltip}
				onOpenChange={isOpen => {
					if (vehicleByValue?.name.length > nameLimit) {
						setShowTooltip(isOpen);
					}
				}}
			>
				<Select
					className="vehicle-item-select"
					showSearch
					showArrow
					onSelect={option => onSelect(option)}
					defaultValue={defaultValue}
					value={value?.id}
					bordered={false}
					disabled={isDisabled}
					filterOption={getFilterOptionOnSearch}
					{...dropdownStyle}
				>
					{getSortedArray(Array.from(vehicles.values()), 'name').map((vehicle: Vehicle) => {
						const vehicleName =
							trailerIdAttachedToVehicle !== vehicle.id
								? vehicle.name
								: `${translations.defaultText}(${vehicle.name})`;

						return (
							<Option
								key={vehicle.id}
								value={vehicle.id}
								disabled={value?.id !== vehicle.id && selectedVehicleIds.has(vehicle.id)}
							>
								<span className={itemClassName} data-test-id={itemClassName}>
									{vehicleName}
								</span>
								{getVehicleNotAvailableIcon(
									vehicle,
									translations.tooltipText,
									unavailableVehicleIds,
									unavailableTrailerIds
								)}
							</Option>
						);
					})}
				</Select>
			</Tooltip>
		</div>
	);
};

interface ServiceAreaSelectProps {
	serviceAreas: ServiceArea[];
	addServiceArea: (id, name) => void;
	removeServiceArea: (id) => void;
	placeholder: string;
	valueText: string;
	prefix?: string;
	unavailableAreas?: number[];
	selectedServiceAreas?: number[];
	className?: string;
}

export const ServiceAreaSelect = ({
	serviceAreas,
	addServiceArea,
	removeServiceArea,
	placeholder,
	valueText,
	unavailableAreas = [],
	selectedServiceAreas = [],
	className = 'service-area-select',
	prefix = 'service-areas'
}: ServiceAreaSelectProps) => {
	const sortedServiceAreas = SortByStringWithNumber(serviceAreas, 'name');
	const [showTooltip, setShowTooltip] = useState(false);
	const [isSelectIsOpen, setIsSelectIsOpen] = useState({ open: false });

	const selectedAreaName = useMemo(
		() => serviceAreas.find(area => selectedServiceAreas[0] === area.id)?.name,
		[selectedServiceAreas?.[0], serviceAreas]
	);

	const getMultiselectCustomValue = () => {
		return (
			<div className="service-area-value-container" data-test-id={`${prefix}-value`}>
				<span className="selected-areas-count">{selectedServiceAreas.length} </span>
				{valueText}
			</div>
		);
	};

	return (
		<div className="service-area-item-container" data-test-id={`${prefix}-select-container`}>
			{selectedServiceAreas.length > 1 && getMultiselectCustomValue()}
			<Tooltip
				title={selectedAreaName}
				open={showTooltip}
				onOpenChange={isOpen => {
					if (
						!isSelectIsOpen.open &&
						selectedServiceAreas.length === 1 &&
						selectedAreaName.length > NAME_LENGTH_LIMIT_WITH_AREAS
					) {
						setShowTooltip(isOpen);
					} else {
						setShowTooltip(false);
					}
				}}
			>
				<Select
					className={classNames(className, {
						'multiple-values': selectedServiceAreas.length > 1
					})}
					mode="multiple"
					showArrow
					onDropdownVisibleChange={open => setIsSelectIsOpen({ open })}
					allowClear={false}
					showSearch
					onSelect={(id, option) => addServiceArea(id, option.name)}
					onDeselect={removeServiceArea}
					filterOption={(input, option) => {
						return option?.name.toLowerCase().includes(input.toLowerCase());
					}}
					bordered={false}
					value={selectedServiceAreas}
					dropdownMatchSelectWidth={false}
					placeholder={placeholder}
				>
					{sortedServiceAreas.map((serviceArea: ServiceArea) => {
						return (
							<Option
								key={serviceArea.id}
								value={serviceArea.id}
								name={serviceArea.name}
								disabled={unavailableAreas.includes(serviceArea.id)}
								data-test-id={`${prefix}-item-${serviceArea.id}`}
							>
								<span className="service-area-item-text">{serviceArea.name}</span>
							</Option>
						);
					})}
				</Select>
			</Tooltip>
		</div>
	);
};

interface DriversSelectProps {
	drivers: Map<number, Driver>;
	selectedDriverIds: Set<number>;
	value: any;
	onSelect: (id) => void;
	defaultId: number;
	tooltipText: string;
	unavailableDriverIds: Set<number>;
	withServiceAreas?: boolean;
}

export const DriversSelect = ({
	drivers,
	selectedDriverIds,
	value,
	onSelect,
	defaultId,
	tooltipText,
	unavailableDriverIds,
	withServiceAreas = false
}: DriversSelectProps) => {
	const defaultValue = drivers.get(defaultId)?.id;
	const itemClassName = 'driver-item-text';
	const [showTooltip, setShowTooltip] = useState(false);
	const driverByValue = drivers.get(value?.id);
	const nameLimit = withServiceAreas ? NAME_LENGTH_LIMIT_WITH_AREAS : NAME_LENGTH_LIMIT;

	return (
		<div className="driver-item-container">
			<Tooltip
				title={driverByValue?.name}
				onOpenChange={isOpen => {
					if (driverByValue?.name.length > nameLimit) {
						setShowTooltip(isOpen);
					}
				}}
				open={showTooltip}
			>
				<Select
					className="driver-item-select"
					showSearch
					showArrow
					onSelect={onSelect}
					defaultValue={defaultValue}
					value={value?.id}
					bordered={false}
					optionFilterProp="children"
					onClick={() => setShowTooltip(false)}
					filterOption={getFilterOptionOnSearch}
					{...dropdownStyle}
				>
					{getSortedArray(Array.from(drivers.values()), 'name').map((driver: Driver) => {
						return (
							<Option
								key={driver.id}
								value={driver.id}
								disabled={value?.id !== driver.id && selectedDriverIds.has(driver.id)}
							>
								<span className={itemClassName} data-test-id={itemClassName}>
									{driver.name}
								</span>

								{getDriverNotAvailableIcon(driver, tooltipText, unavailableDriverIds)}
							</Option>
						);
					})}
				</Select>
			</Tooltip>
		</div>
	);
};

export const getWarningIcon = () => {
	return (
		<BringgIcon
			data-test-id="warning-icon"
			className="delivery-block-select-driver-info-icon"
			iconName={BringgFontIcons.Info}
		/>
	);
};

export const getDriverNotAvailableIcon = (driver: Driver, tooltipText: string, unavailableDriverIds: Set<number>) => {
	return (
		!!driver.id && unavailableDriverIds.has(driver.id) && <Tooltip title={tooltipText}>{getWarningIcon()}</Tooltip>
	);
};

export const getVehicleNotAvailableIcon = (
	vehicle: Vehicle,
	tooltipText: string,
	unavailableVehicleIds: Set<number>,
	unavailableTrailerIds: Set<number>
) => {
	return !!vehicle.id && vehicle.is_trailer
		? unavailableTrailerIds.has(vehicle.id)
		: unavailableVehicleIds.has(vehicle.id) && <Tooltip title={tooltipText}>{getWarningIcon()}</Tooltip>;
};

const getSortedArray = (array: any, key: string) => {
	const firstSelectItem = array[0];
	array.shift();
	SortByStringWithNumber(array, key);
	array.unshift(firstSelectItem);
	return array;
};

const getFilterOptionOnSearch = (input, option) => filterOption(input, option.children[0].props);
